import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Breadcrumbs from "@/components/Breadcrumbs";
import EstimateForm from "@/components/EstimateForm";
import { Reveal } from "@/components/Reveal";
import { ServiceAreaSection } from "@/components/Sections";
import { EstimateButton, CallButton, TextButton } from "@/components/CTAButtons";
import { business, telLink, hoursList } from "@/data/site";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, PawPrint } from "lucide-react";

const contactItems = [
  { icon: Phone, label: "Phone", value: business.phoneDisplay, href: telLink },
  { icon: Mail, label: "Email", value: business.email, href: `mailto:${business.email}` },
  { icon: MapPin, label: "Service Area", value: business.serviceArea },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact & Free Estimate | Benji's Hauling & Junk Removal"
        description="Ready to clear the clutter? Call, text, or request a free junk removal estimate online. Full-service loading, hauling, and cleanup, starting at $99."
        path="/contact"
      />
      <Breadcrumbs items={[{ label: "Contact" }]} />
      <PageHero
        eyebrow="Contact"
        title="Ready to Clear the Clutter?"
        sub="Call, text, or send us a few details about what you need removed. Free estimates are always available."
      >
        <div className="flex flex-wrap gap-3">
          <CallButton variant="primary" />
          <TextButton variant="ghostLight" />
          <EstimateButton label="Request Free Estimate" to="#estimate" variant="forest" className="border border-cream/25" />
        </div>
      </PageHero>

      <section className="bg-cream py-20">
        <div className="container-x">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
            {/* Contact details */}
            <Reveal>
              <h2 className="font-heading text-2xl font-extrabold tracking-tight text-forest">Get in Touch</h2>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                Prefer to talk it through? Reach out any way that's easiest for you.
              </p>
              <ul className="mt-8 space-y-4">
                {contactItems.map((c) => (
                  <li key={c.label} className="flex items-start gap-4 rounded-lg border border-border bg-white p-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-gold/15 text-gold-dark">
                      <c.icon className="h-5 w-5" strokeWidth={2} />
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-mutedgreen">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} className="font-heading text-lg font-bold text-forest hover:text-gold-dark" data-testid={`contact-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>{c.value}</a>
                      ) : (
                        <p className="font-heading text-lg font-bold text-forest">{c.value}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Hours */}
              <div className="mt-4 rounded-lg border border-border bg-white p-5" data-testid="contact-hours">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gold-dark" strokeWidth={2} />
                  <p className="text-xs font-bold uppercase tracking-wide text-mutedgreen">Hours</p>
                </div>
                <ul className="mt-3 divide-y divide-border/70">
                  {hoursList.map((h) => (
                    <li key={h.day} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="font-semibold text-forest">{h.day}</span>
                      <span className={h.time === "Closed" ? "text-mutedgreen" : "text-charcoal/75"}>{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-lg border border-border bg-accent/40 p-5">
                <p className="text-xs font-bold uppercase tracking-wide text-mutedgreen">Follow Benji</p>
                <div className="mt-3 flex flex-col gap-3">
                  <a href={business.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="inline-flex items-center gap-3 text-forest hover:text-gold-dark">
                    <Instagram className="h-5 w-5" /> <span className="font-semibold">Benji's Hauling &amp; Junk Removal</span>
                  </a>
                  <a href={business.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" className="inline-flex items-center gap-3 text-forest hover:text-gold-dark">
                    <Facebook className="h-5 w-5" /> <span className="font-semibold">Benji's Hauling &amp; Junk Removal</span>
                  </a>
                </div>
              </div>
            </Reveal>

            {/* Estimate form */}
            <Reveal delay={0.1}>
              <div id="estimate" className="scroll-mt-24">
                <div className="mb-6 flex items-center gap-2">
                  <PawPrint className="h-5 w-5 text-gold-dark" strokeWidth={2.4} />
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight text-forest">Request Your Free Estimate</h2>
                </div>
                <p className="mb-6 max-w-xl text-sm leading-relaxed text-charcoal/70">
                  Tell us what you need removed and we'll help you determine the best option. For the fastest
                  estimate, mention that you'd like to text us a few photos of the items.
                </p>
                <EstimateForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <ServiceAreaSection dark />
    </>
  );
}
